<template>
  <v-dialog :value="show" persistent max-width="732px" content-class="event-password-dialog">
    <v-card class="password-dialog">
      <form @submit.prevent="submitForm">
        <v-card-title>
          <div class="title-container">
            <h2 class="modal-title">{{ title }}</h2>
            <button
              type="button"
              class="modal-close-icon"
              :aria-label="adminText('organizationAll.close_bbfa773e', 'Close')"
              :disabled="saving"
              @click="closeDialog()"
            >
              <img src="@/assets/icons/event-password/close.svg" alt="">
            </button>
          </div>
        </v-card-title>

        <v-card-text class="password-fields">
          <text-field
            v-model="form.password"
            outline
            :disabled="saving"
            :label="adminText('organizationAll.password_37d602a3', 'Password*')"
            :error-messages="{
              required: adminText('organizationAll.this_field_is_required_ab90d9d7', 'This field is required.'),
              maxLength: adminText('organizationAll.maximum_30_characters_allowed_31872224', 'Maximum 30 characters allowed.'),
              minLength: adminText('organizationAll.enter_at_least_5_characters', 'Enter at least 5 characters.'),
            }"
            :validator="$v.form.password"
          >
            <button
              slot="append"
              type="button"
              class="password-copy"
              :disabled="!form.password || saving"
              :aria-label="adminText('organizationAll.copy_password', 'Copy password')"
              @click="$emit('onCopyPassword', form.password)"
            >
              <img src="@/assets/icons/event-password/copy.svg" alt="">
            </button>
          </text-field>

          <datetime-field
            v-model="form.startsAt"
            outline
            minute-precision
            :disabled="saving"
            :validator="$v.form.startsAt"
            :format="dateFormat"
            :error-messages="dateErrors"
            :label="adminText('organizationAll.start_date_2fde03a5', 'Start date*')"
          >
            <img slot="append" src="@/assets/icons/event-password/calendar.svg" alt="">
          </datetime-field>

          <datetime-field
            v-model="form.expiresAt"
            outline
            minute-precision
            :disabled="saving"
            :validator="$v.form.expiresAt"
            :format="dateFormat"
            :error-messages="dateErrors"
            :label="adminText('organizationAll.end_date_dc7fcbdc', 'End date*')"
          >
            <img slot="append" src="@/assets/icons/event-password/calendar.svg" alt="">
          </datetime-field>
        </v-card-text>

        <p v-if="saveError" class="password-save-error" role="alert">{{ saveError }}</p>

        <v-card-actions>
          <div class="actions-container" :class="{ 'actions-container-edit': password }">
            <main-button
              v-if="password"
              flat
              depressed
              :text="adminText('organizationAll.delete_f6fdbe48', 'Delete')"
              color="error"
              type="button"
              :disabled="saving"
              @click="$emit('onDeletePassword', password)"
            />
            <main-button
              :text="organizationText(submitBtnLabel)"
              color="primary"
              type="submit"
              depressed
              :loading="saving"
              :disabled="saving"
            />
          </div>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>
<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { maxLength, minLength, required } from 'vuelidate/lib/validators'
import { date } from './../../validators'
import { isPasswordDateRangeValid } from '@/utils/event-passwords'

export default {
  mixins: [OrganizationLocalization],
  name: 'event-password-form-modal',
  props: {
    show: Boolean,
    title: String,
    saving: Boolean,
    saveError: {
      type: String,
      default: '',
    },
    password: {
      type: Object,
      default: () => null,
    },
  },
  emits: ['onCreate', 'onUpdate', 'update:show', 'onClearEditPassword', 'onDeletePassword', 'onCopyPassword'],
  data() {
    return {
      form: {
        id: this.password ? this.password.id : null,
        password: this.password ? this.password.password : '',
        startsAt: this.password ? this.password.startsAt : null,
        expiresAt: this.password ? this.password.expiresAt : null,
      },
    }
  },
  validations() {
    return {
      form: {
        password: {
          required,
          minLength: minLength(5),
          maxLength: maxLength(30),
        },
        startsAt: {
          required,
          date,
        },
        expiresAt: {
          required,
          date,
          dateRange: value => !value || !this.form.startsAt || !date(value) || !date(this.form.startsAt) || isPasswordDateRangeValid(this.form.startsAt, value),
        },
      },
    }
  },
  computed: {
    dateFormat() {
      if (this.$vuetify && this.$vuetify.breakpoint.xsOnly) return 'DD.MM.YYYY. HH:mm'

      return this.isAdminLocalization && this.$adminLocale() === 'hr'
        ? 'dddd, D. MMMM YYYY., HH:mm'
        : 'dddd, MMMM Do YYYY, HH:mm'
    },
    dateErrors() {
      return {
        required: this.adminText('organizationAll.this_field_is_required_ab90d9d7', 'This field is required.'),
        date: this.adminText('organizationAll.password_invalid_date', 'Enter a valid date and time.'),
        dateRange: this.adminText('organizationAll.password_invalid_range', 'End date and time must be after start date and time.'),
      }
    },
    submitBtnLabel() {
      return this.password ? 'Update' : 'Create password'
    },
  },
  methods: {
    submitForm() {
      if (this.saving) return
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit(this.password ? 'onUpdate' : 'onCreate', {
        ...this.form,
        startsAt: new Date(this.form.startsAt).toISOString(),
        expiresAt: new Date(this.form.expiresAt).toISOString(),
      })
    },
    closeDialog() {
      if (this.saving) return
      this.$emit('update:show', false)
      this.clearPasswordProp()
    },
    clearPasswordProp() {
      this.$emit('onClearEditPassword')
    },
  },
}
</script>

<style scoped>
.password-dialog {
  padding: 24px;
  border-radius: 12px;
}

.password-dialog form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.password-dialog .v-card__title,
.password-dialog .v-card__text,
.password-dialog .v-card__actions {
  padding: 0;
}

.password-fields {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.password-fields >>> .v-input {
  margin: 0;
  padding: 0;
}

.password-fields >>> .v-input__slot {
  min-height: 56px;
  margin: 0;
  padding: 0 16px !important;
  border: 1px solid #d9d9d9 !important;
  border-radius: 4px;
}

.password-fields >>> .v-input--is-focused .v-input__slot {
  border-color: #0277bd !important;
}

.password-fields >>> .error--text .v-input__slot {
  border-color: #db384c !important;
}

.password-fields >>> .v-text-field__details {
  min-height: 0;
  padding: 0 12px;
  margin: 0;
}

.password-fields >>> .v-messages {
  min-height: 0;
}

.password-fields >>> .v-input__append-inner {
  margin: 16px 0 0 12px;
}

.password-fields >>> input,
.password-fields >>> label {
  font-family: Roboto, sans-serif;
  letter-spacing: 0.5px;
}

.password-fields >>> input {
  align-self: center;
  height: 24px;
  margin-top: 0;
  padding: 0;
  line-height: 24px;
}

.password-fields >>> .v-label {
  top: 15px;
  height: 24px;
  line-height: 24px;
}

.password-fields >>> .v-label--active {
  padding: 0 4px;
  margin-left: -4px;
  background: white;
  transform: translateY(-24px) scale(0.75);
}

.password-copy,
.modal-close-icon {
  display: flex;
  flex: 0 0 24px;
}

.password-copy:focus-visible,
.modal-close-icon:focus-visible {
  outline: 2px solid #0277bd;
}

.modal-title {
  margin: 0;
  color: #0277BD;
  font-weight: 600;
  font-size: 20px;
  font-family: 'Raleway', sans-serif;
  line-height: 24px;
  letter-spacing: -0.6px;
}

.title-container {
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.actions-container {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.actions-container-edit {
  justify-content: space-between;
}

.actions-container >>> .v-btn {
  height: 48px;
  margin: 0;
  padding: 12px 24px;
  letter-spacing: -0.27px;
}

.password-save-error {
  margin: 0;
  color: #db384c;
}

@media (max-width: 600px) {
  .password-dialog {
    padding: 20px 16px;
  }
  .modal-title {
    font-size: 18px;
  }
}
</style>

<style>
.v-dialog.event-password-dialog {
  border-radius: 12px;
  box-shadow: 0 0 24px rgba(51, 51, 51, 0.2);
}
</style>
